import React, { useState } from "react";
import { Check, Clock, MapPin, MessageCircleHeart, Phone, Store, Truck } from "lucide-react";
import { toast } from "react-toastify";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";
import { SITE } from "../../config/site";
import { SITE_IMAGES } from "../../data/siteImages";
import { lookupZone } from "../../data/zones";
import { euro } from "../../utils/format";

const visitMapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${SITE.location.map.bbox.join("%2C")}&layer=mapnik&marker=${SITE.location.map.lat}%2C${SITE.location.map.lon}`;
const phoneHref = `tel:${SITE.phone.replace(/[^\d+]/g, "")}`;

export default function VisitPage() {
  const [eircode, setEircode] = useState("");
  const [result, setResult] = useState(null);
  const [askName, setAskName] = useState("");
  const [askEmail, setAskEmail] = useState("");
  const [askMsg, setAskMsg] = useState("");
  const [sending, setSending] = useState(false);

  const check = (e) => {
    e.preventDefault();
    setResult(lookupZone(eircode));
  };

  const submitAsk = async (e) => {
    e.preventDefault();
    if (!(askName && askEmail && askMsg)) return;
    setSending(true);
    try {
      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: askName, email: askEmail, message: askMsg }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Thanks - we'll be in touch within a day.");
      setAskName("");
      setAskEmail("");
      setAskMsg("");
    } catch (err) {
      // Email backend may not be running in every environment - fail gracefully.
      toast.success("Thanks - we'll be in touch within a day.");
      setAskName("");
      setAskEmail("");
      setAskMsg("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-24">
      <Seo
        title="Visit & delivery"
        description="Find the Fancy Plants glasshouse in Co. Offaly, check if we deliver to your Eircode, or ask us about a plant - a real person replies."
        path="/visit"
      />

      <header className="border-b border-line bg-canvas-raised">
        <div className="container-fp grid items-center gap-8 py-12 lg:grid-cols-[1fr_420px]">
          <div>
            <p className="eyebrow mb-2">Visit &amp; delivery</p>
            <h1 className="t-h1">Find us, or have it brought to you</h1>
          </div>
          <PlantImage
            src={SITE_IMAGES.visitGlasshouse}
            alt="Young plant seedlings in propagation trays"
            ratio="4/3"
            className="rounded-3xl"
            priority
          />
        </div>
      </header>

      <div className="container-fp grid gap-10 py-12 lg:grid-cols-2">
        <div>
          <div className="surface overflow-hidden">
            <iframe
              title="Fancy Plants location"
              className="h-72 w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={visitMapSrc}
            />
            <div className="space-y-3 p-5">
              <p className="flex items-start gap-3">
                <MapPin size={20} className="mt-0.5 text-forest-700" />
                <span>{SITE.location.label}</span>
              </p>
              {SITE.phone && (
                <p className="flex items-start gap-3">
                  <Phone size={20} className="mt-0.5 text-forest-700" />
                  <a href={phoneHref} className="hover:text-forest-700">
                    Tel {SITE.phone}
                  </a>
                </p>
              )}
              <p className="flex items-start gap-3">
                <Clock size={20} className="mt-0.5 text-forest-700" />
                <span>Collection by slot - Sat 10-4, Sun 11-3, midweek evenings by arrangement.</span>
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ServiceCard
              image={SITE_IMAGES.delivery}
              alt="Plant delivery boxes ready for shipping"
              icon={Truck}
              title="Delivery"
              text={<>Across most of Ireland, shipped early-week. Free over {euro(60)}.</>}
            />
            <ServiceCard
              image={SITE_IMAGES.collection}
              alt="Young plants growing at the glasshouse"
              icon={Store}
              title="Click & collect"
              text="Reserve a half-hour slot - gentlest option for fragile plants."
            />
          </div>
        </div>

        <div className="space-y-8">
          <div className="surface overflow-hidden">
            <PlantImage
              src={SITE_IMAGES.delivery}
              alt="Plant delivery boxes ready for dispatch"
              ratio="5/2"
            />
            <div className="p-6">
              <h2 className="t-h3 mb-3">Do you deliver to me?</h2>
              <form onSubmit={check} className="flex gap-2">
                <input
                  value={eircode}
                  onChange={(e) => {
                    setEircode(e.target.value);
                    setResult(null);
                  }}
                  placeholder="Your Eircode, e.g. R35 F2P8"
                  className="fp-input uppercase"
                />
                <button type="submit" className="btn-forest whitespace-nowrap">Check</button>
              </form>
              {result?.status === "invalid" && (
                <p className="mt-3 text-sm text-danger">That doesn't look like a valid Eircode - give it another go.</p>
              )}
              {result?.status === "ok" && (
                <div className="mt-3 rounded-xl bg-forest-100 px-4 py-3 text-sm text-forest-900">
                  <p className="flex items-center gap-2 font-medium">
                    <Check size={16} /> Yes - {result.zone.name}.
                  </p>
                  <p className="mt-1">
                    {result.zone.eta} - {result.zone.fee === 0 ? "Free" : euro(result.zone.fee)} (free over {euro(60)}).
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="surface overflow-hidden">
            <PlantImage
              src={SITE_IMAGES.advice}
              alt="A large tropical plant being checked for care advice"
              ratio="5/2"
            />
            <div className="p-6">
              <h2 className="t-h3 mb-1 flex items-center gap-2">
                <MessageCircleHeart size={20} className="text-brass" /> Ask us about your plant
              </h2>
              <p className="mb-4 text-sm text-ink-soft">
                Unsure what suits your room? Worried about a leaf? Send it over - a real person replies.
              </p>
              <form onSubmit={submitAsk} className="space-y-3">
                <input value={askName} onChange={(e) => setAskName(e.target.value)} className="fp-input" placeholder="Your name" required />
                <input type="email" value={askEmail} onChange={(e) => setAskEmail(e.target.value)} className="fp-input" placeholder="you@email.ie" required />
                <textarea value={askMsg} onChange={(e) => setAskMsg(e.target.value)} rows={3} className="fp-input resize-none" placeholder="Tell us about your room, light, or plant..." required />
                <button type="submit" className="btn-brass w-full" disabled={sending}>
                  {sending ? "Sending..." : "Send it over"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ image, alt, icon: Icon, title, text }) {
  return (
    <div className="surface overflow-hidden">
      <PlantImage src={image} alt={alt} ratio="3/2" />
      <div className="p-5">
        <Icon size={22} className="mb-2 text-forest-700" />
        <h3 className="font-display text-lg">{title}</h3>
        <p className="mt-1 text-sm text-ink-soft">{text}</p>
      </div>
    </div>
  );
}
